import { generateToDocFn } from '@dadohr/shared/domainTypes/document';
import { UserChangeThroughImport } from '@dadohr/shared/domainTypes/user';
import { FS } from '@dadohr/shared/versions';
import { sortBy } from 'lodash';
import { useCollection } from 'react-firebase-hooks/firestore';
import {
  refreshTimestamp,
  store,
  useMappedLoadingValue
} from '../../../../services/firebase';
import { useFirebaseFunction } from '../../../../services/firebaseFunctions';
import { timestampToMillisSafe, Timestamp } from '../../../../services/time';

export const useUserHistoryData = (orgId: string, userId: string) => {
  return useMappedLoadingValue(
    useFirebaseFunction<{ createTime: Timestamp }>(
      'user-getAdditionalUserData',
      { userId, orgId },
      [userId, orgId]
    ),
    (x) => ({
      createTime: refreshTimestamp(x.createTime)
    })
  );
};

/**
 * A merge writes a record to the same `userChangesV1` stream the History tab
 * reads, discriminated by `type: 'M'`. It has a different shape than an import
 * change (no `committedAt`/`importId`).
 */
export type UserChangeThroughMerge = {
  userId: string; // the surviving user
  changes: {};
  orgId: string;
  type: 'M';
  mergedAt: Timestamp;
  mergedFromUserId: string; // the deleted (losing) user's id
  executor: string; // the admin's userId
};

export type UserChangeHistoryItem =
  | UserChangeThroughImport
  | UserChangeThroughMerge;

const toUserChangeDoc = generateToDocFn<UserChangeHistoryItem>((d) => {
  // Only import records default to 'C' when the discriminator is missing;
  // merge records always carry an explicit 'M'.
  if (!d.type) {
    (d as UserChangeThroughImport).type = 'C';
  }
  return d;
});

const changeSortMillis = (c: UserChangeHistoryItem): number =>
  timestampToMillisSafe(
    c.type === 'M' ? c.mergedAt : (c as UserChangeThroughImport).committedAt
  ) || 0;

export const useUserImportHistory = (orgId: string, userId: string) => {
  return useMappedLoadingValue(
    useCollection(
      store()
        .collection(FS.userChanges)
        .where('orgId', '==', orgId)
        .where('userId', '==', userId)
    ),
    (s) => sortBy(s.docs.map(toUserChangeDoc), (x) => -changeSortMillis(x.data))
  );
};
